import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronsLeftRight, ArrowRight, Phone, CircleCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
export default function CompleteRefurbishments() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const newPos = Math.max(0, Math.min(100, x / rect.width * 100));
    setSliderPosition(newPos);
  }, []);
  const onMouseDown = () => setIsDragging(true);
  const onTouchStart = () => setIsDragging(true);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      handleMove(e.touches[0].clientX);
    };
    const handleUp = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('touchend', handleUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDragging, handleMove]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      setSliderPosition(prev => Math.max(0, prev - 5));
    } else if (e.key === 'ArrowRight') {
      setSliderPosition(prev => Math.min(100, prev + 5));
    }
  };
  return <section id="complete-refurbishments" className="relative w-full min-h-[700px] h-[calc(100vh-80px)] overflow-hidden bg-white select-none" aria-label="Bathroom renovation before and after comparison">
      <div ref={containerRef} className="relative w-full h-full group cursor-crosshair" onKeyDown={handleKeyDown} tabIndex={0} role="slider" aria-valuenow={sliderPosition} aria-valuemin={0} aria-valuemax={100} aria-label="Drag to compare before and after">
        {}
        <div className="absolute inset-0 w-full h-full">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/bathroom_renovation_before.png" alt="Bathroom before renovation" className="w-full h-full object-cover grayscale-[20%]" />
          {}
          <div className="absolute bottom-8 left-8 bg-black/60 backdrop-blur-sm text-foreground px-4 py-2 rounded-md text-sm font-medium tracking-wider uppercase pointer-events-none">
            Before
          </div>
        </div>

        {}
        <div className="absolute inset-0 w-full h-full overflow-hidden" style={{
        clipPath: `inset(0 0 0 ${sliderPosition}%)`
      }}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/bathroom_renovation_after.png" alt="Bathroom after renovation" className="absolute inset-0 w-full h-full object-cover" />
          
          {}
          <div className="absolute inset-0 bg-black/10" /> {}
          
          <div className="absolute right-0 top-0 bottom-0 w-full md:w-[60%] lg:w-[50%] flex items-center justify-center md:justify-end p-6 md:p-12 lg:p-20">
            <div className="bg-primary/95 backdrop-blur-md text-primary-foreground p-8 md:p-10 rounded-sm shadow-2xl max-w-xl border-l-4 border-accent animate-in fade-in slide-in-from-right-10 duration-700">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight font-sans">
                Professional Tiling & Bathroom Renovations
              </h2>
              <p className="text-lg md:text-xl text-primary-foreground/90 mb-8 leading-relaxed">
                Specialists in luxury tile installations, wet rooms and walk-in showers with over 11 years experience.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/GetAQuotePage">
                  <Button size="lg" className="w-full sm:w-auto text-base font-semibold bg-accent text-accent-foreground hover:bg-accent/90 border-none shadow-lg">
                    Get a Free Quote
                    <ArrowRight className="ml-2 size-5" />
                  </Button>
                </Link>
                <Link to="tel:+447467562688">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto text-base border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-foreground">
                    <Phone className="mr-2 size-5" />
                    +447467562688
                  </Button>
                </Link>
              </div>

              <div className="mt-8 pt-6 border-t border-primary-foreground/20 grid grid-cols-2 gap-4">
                {['Luxury Finishes', 'Wet Rooms', 'Precision Tiling', '11+ Years Exp.'].map((feature, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-sm font-medium text-primary-foreground/80">
                    <CircleCheck className="size-4 text-accent" />
                    {feature}
                  </div>)}
              </div>
            </div>
          </div>

          {}
          <div className="absolute bottom-8 right-8 bg-accent text-accent-foreground px-4 py-2 rounded-md text-sm font-bold tracking-wider uppercase shadow-lg pointer-events-none">
            After
          </div>
        </div>

        {}
        <div className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-30 shadow-[0_0_10px_rgba(0,0,0,0.5)] hover:bg-accent transition-colors" style={{
        left: `${sliderPosition}%`
      }} onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-12 md:size-14 bg-white rounded-full shadow-xl flex items-center justify-center border-4 border-primary/10 hover:scale-110 transition-transform">
            <ChevronsLeftRight className="size-6 md:size-8 text-primary" />
          </div>
        </div>
      </div>
    </section>;
}